import React from "react";
import { Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { Phone, Check, ArrowRight, FileText, Users, Banknote, ClipboardList } from "lucide-react";
import { companyInfo as mockCompany, funeralAid as mockFuneralAid, galleryImages } from "../data/mock";
import { useCompany } from "../hooks/useContent";
import PageHero from "../components/PageHero";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "../components/ui/accordion";

export default function FuneralAidPage() {
  const { data: companyData } = useCompany();
  const funeralAid = companyData.funeralAid;
  const companyInfo = companyData.companyInfo;
  return (
    <div>
      <Helmet>
        <title>Ajutor de Înmormântare 2026 – Ghid Complet | Casa Funerară Sfântul Andrei</title>
        <meta name="description" content="Ghid complet ajutor de înmormântare 2026: beneficiari, valoare, acte necesare și procedura pas cu pas. Consiliere gratuită la Casa Funerară Sfântul Andrei Constanța." />
        <link rel="canonical" href="https://funeraresfantulandrei.ro/ajutor-inmormantare-2026" />
        <meta property="og:title" content="Ajutor de Înmormântare 2026 – Ghid Complet | Casa Funerară Sfântul Andrei" />
        <meta property="og:url" content="https://funeraresfantulandrei.ro/ajutor-inmormantare-2026" />
      </Helmet>
      <PageHero
        image={galleryImages.sfAndreiGrau}
        title={funeralAid.title}
        subtitle={funeralAid.intro}
        breadcrumbs={[
          { label: "Acasă", to: "/" },
          { label: "Ajutor de Înmormântare 2026" },
        ]}
      />

      {/* Main Content */}
      <section className="section-spacing bg-white">
        <div className="max-w-[1140px] mx-auto px-4 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-16">
            <div className="lg:col-span-2 space-y-16">
              {/* Beneficiari */}
              <div>
                <div className="flex items-center gap-3 mb-8">
                  <div className="w-10 h-10 flex items-center justify-center bg-[#0f0f0f]">
                    <Users className="w-4 h-4 text-white" />
                  </div>
                  <h2
                    className="text-2xl font-semibold"
                    style={{ fontFamily: "'Playfair Display', serif" }}
                  >
                    Cine Beneficiază
                  </h2>
                </div>
                <div className="space-y-3">
                  {funeralAid.beneficiari.map((item, i) => (
                    <div key={i} className="flex items-start gap-3 p-4 bg-[#FAFAF8] border border-[#E8E4DC]">
                      <Check className="w-4 h-4 text-[#0f0f0f] mt-0.5 flex-shrink-0" />
                      <span className="text-sm text-[#666666]">{item}</span>
                    </div>
                  ))}
                </div>
              </div>

              {/* Acte Necesare */}
              <div>
                <div className="flex items-center gap-3 mb-8">
                  <div className="w-10 h-10 flex items-center justify-center bg-[#0f0f0f]">
                    <FileText className="w-4 h-4 text-white" />
                  </div>
                  <h2
                    className="text-2xl font-semibold"
                    style={{ fontFamily: "'Playfair Display', serif" }}
                  >
                    Acte Necesare
                  </h2>
                </div>
                <div className="space-y-3">
                  {funeralAid.acteNecesare.map((item, i) => (
                    <div key={i} className="flex items-start gap-4 p-4 border border-[#E8E4DC]">
                      <span className="text-xs font-semibold text-[#999999] w-6 text-right flex-shrink-0">
                        {String(i + 1).padStart(2, '0')}
                      </span>
                      <span className="text-sm text-[#666666]">{item}</span>
                    </div>
                  ))}
                </div>
              </div>

              {/* Valoare */}
              <div className="p-8 bg-[#FAFAF8] border border-[#E8E4DC]">
                <div className="flex items-center gap-3 mb-4">
                  <Banknote className="w-5 h-5 text-[#0f0f0f]" />
                  <h2
                    className="text-xl font-semibold"
                    style={{ fontFamily: "'Playfair Display', serif" }}
                  >
                    Valoare Estimativă
                  </h2>
                </div>
                <p className="text-sm text-[#666666] leading-relaxed">{funeralAid.valoare}</p>
              </div>

              {/* Procedura */}
              <div>
                <div className="flex items-center gap-3 mb-8">
                  <div className="w-10 h-10 flex items-center justify-center bg-[#0f0f0f]">
                    <ClipboardList className="w-4 h-4 text-white" />
                  </div>
                  <h2
                    className="text-2xl font-semibold"
                    style={{ fontFamily: "'Playfair Display', serif" }}
                  >
                    Procedura – Pas cu Pas
                  </h2>
                </div>
                <div className="space-y-4">
                  {funeralAid.procedura.map((step, i) => (
                    <div key={i} className="flex items-start gap-5">
                      <div className="w-10 h-10 flex items-center justify-center border-2 border-[#0f0f0f] flex-shrink-0">
                        <span className="text-sm font-bold text-[#0f0f0f]">{i + 1}</span>
                      </div>
                      <div className="pt-2">
                        <p className="text-sm text-[#666666] font-medium">{step}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Plata Direct */}
              <div className="p-8 bg-[#0f0f0f] text-white">
                <h3
                  className="text-xl font-semibold mb-4 text-white"
                  style={{ fontFamily: "'Playfair Display', serif" }}
                >
                  Posibilitate Plată Direct prin Ajutor
                </h3>
                <p className="text-sm text-white/60 leading-relaxed mb-6">
                  La Casa Funerară Sfântul Andrei, acceptăm plata serviciilor funerare direct
                  din ajutorul de înmormântare. Nu trebuie să suportați costurile din buzunar –
                  ne ocupăm noi de toate formalitățile.
                </p>
                <a
                  href={`tel:${mockCompany.phone}`}
                  className="inline-flex items-center gap-2 bg-white text-[#0f0f0f] px-6 py-3 text-sm font-semibold no-underline hover:bg-white/90 transition-colors"
                  style={{ borderRadius: "var(--radius-pill)" }}
                >
                  <Phone className="w-4 h-4" />
                  Consiliere Gratuită
                </a>
              </div>

              {/* FAQ */}
              <div className="faq-section">
                <h2
                  className="text-2xl font-semibold mb-8"
                  style={{ fontFamily: "'Playfair Display', serif" }}
                >
                  Întrebări Frecvente
                </h2>
                <Accordion type="single" collapsible className="space-y-3">
                  {funeralAid.faqs.map((faq, i) => (
                    <AccordionItem
                      key={i}
                      value={`faq-${i}`}
                      className="border border-[#E8E4DC] px-6 data-[state=open]:border-[#cccccc] transition-colors"
                    >
                      <AccordionTrigger className="text-sm font-semibold text-[#0f0f0f] hover:no-underline py-5">
                        {faq.q}
                      </AccordionTrigger>
                      <AccordionContent className="text-sm text-[#666666] leading-relaxed pb-5">
                        {faq.a}
                      </AccordionContent>
                    </AccordionItem>
                  ))}
                </Accordion>
              </div>
            </div>

            {/* Sidebar */}
            <div className="lg:col-span-1">
              <div className="sticky top-36 space-y-8">
                <div className="bg-[#0f0f0f] text-white p-8">
                  <h3
                    className="text-xl font-semibold mb-4 text-white"
                    style={{ fontFamily: "'Playfair Display', serif" }}
                  >
                    Consiliere Gratuită
                  </h3>
                  <p className="text-sm text-white/60 leading-relaxed mb-6">
                    Vă ajutăm gratuit cu întocmirea dosarului pentru ajutorul de înmormântare.
                    Sunați-ne oricând.
                  </p>
                  <a
                    href={`tel:${mockCompany.phone}`}
                    className="flex items-center justify-center gap-2 bg-white text-[#0f0f0f] py-3 text-sm font-semibold no-underline hover:bg-white/90 transition-colors w-full"
                    style={{ borderRadius: "var(--radius-pill)" }}
                  >
                    <Phone className="w-4 h-4" />
                    {mockCompany.phone}
                  </a>
                </div>
                <div className="border border-[#E8E4DC] p-8">
                  <h4 className="text-sm font-semibold uppercase tracking-[2px] text-[#999999] mb-6">
                    Servicii Conexe
                  </h4>
                  <div className="space-y-4">
                    <Link to="/servicii/consiliere-ajutor-inmormantare" className="flex items-center justify-between text-sm text-[#666666] no-underline hover:text-[#0f0f0f] transition-colors py-2 border-b border-[#f0f0f0]">
                      <span>Consiliere Ajutor</span>
                      <ArrowRight className="w-3 h-3" />
                    </Link>
                    <Link to="/servicii/intocmire-acte-deces-documentatie" className="flex items-center justify-between text-sm text-[#666666] no-underline hover:text-[#0f0f0f] transition-colors py-2 border-b border-[#f0f0f0]">
                      <span>Acte de Deces</span>
                      <ArrowRight className="w-3 h-3" />
                    </Link>
                    <Link to="/servicii/servicii-complete-inmormantare-constanta" className="flex items-center justify-between text-sm text-[#666666] no-underline hover:text-[#0f0f0f] transition-colors py-2">
                      <span>Servicii Complete</span>
                      <ArrowRight className="w-3 h-3" />
                    </Link>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="cta-section">
        <div className="max-w-[1140px] mx-auto px-4 lg:px-8 text-center relative z-10">
          <h2
            className="text-3xl sm:text-4xl font-semibold mb-6 text-white"
            style={{ fontFamily: "'Playfair Display', serif" }}
          >
            Sună pentru Consiliere Gratuită
          </h2>
          <p className="text-white/60 max-w-xl mx-auto mb-10">
            Vă ghidăm pas cu pas în obținerea ajutorului de înmormântare.
            Serviciul de consiliere este gratuit.
          </p>
          <a
            href={`tel:${mockCompany.phone}`}
            className="inline-flex items-center gap-3 bg-white text-[#0f0f0f] px-10 py-4 text-sm font-semibold tracking-wide no-underline hover:bg-white/90 transition-colors"
            style={{ borderRadius: "var(--radius-pill)" }}
          >
            <Phone className="w-4 h-4" />
            {mockCompany.phone}
          </a>
        </div>
      </section>
    </div>
  );
}
